import classNames from 'classnames';
import parse, { Element, domToReact } from 'html-react-parser';
import Link from 'next/link';

import styles from 'molecules/RichText/richText.module.scss';

import { isNonEmptyArray, kebabCase } from 'utils/functions';

import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';

interface RichTextProps extends ComponentPropsWithoutRef<'div'> {
  backgroundColor?: string;
  children?: ReactNode;
  light?: boolean;
  content: string;
}

const learningCenter = process.env.NEXT_PUBLIC_LEARNING_CENTER_SITE_DOMAIN || '';

const options = (): HTMLReactParserOptions => ({
  replace(node) {
    if (!('name' in node) || !(node instanceof Element)) {
      return null;
    }

    if (node.name === 'br') {
      return <br />;
    }

    const childNodes = node.children as DOMNode[];

    if (!isNonEmptyArray(childNodes)) {
      return <></>;
    }

    switch (node.name) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6': {
        const content = domToReact(childNodes, options()),
          stringContent = typeof content === 'string' ? content : '',
          id = kebabCase(stringContent);

        const className = node.attribs?.class || '';

        return (
          <node.name className={className} id={kebabCase(id)}>
            {domToReact(childNodes, options())}
          </node.name>
        );
      }
      case 'ul':
        return <ul>{domToReact(childNodes, options())}</ul>;
      case 'ol':
        return <ol>{domToReact(childNodes, options())}</ol>;
      case 'li': {
        const { name: parentElement } = childNodes[0]?.parent?.parent as { name: string };

        return parentElement === 'ul' ? (
          <li>{domToReact(childNodes, options())}</li>
        ) : (
          <li>{domToReact(childNodes, options())}</li>
        );
      }
      case 'p':
        return <p>{domToReact(childNodes, options())}</p>;
      case 'a': {
        const href = node.attribs?.href || '#';
        const isMyMagmutual = href.startsWith('/myMagmutual');

        const resolvedHref = isMyMagmutual ? `${href.replace('/myMagmutual', learningCenter)}` : href;

        return (
          <Link
            href={resolvedHref}
            target={node.attribs?.target || '_self'}
            rel={node.attribs?.rel || (node.attribs?.target === '_blank' ? 'noopener noreferrer' : undefined)}
          >
            {domToReact(childNodes, options())}
          </Link>
        );
      }
      default:
        return node;
    }
  },
});

const RichText: FC<RichTextProps> = ({ children, className, light, content, backgroundColor }) => {
  if (content === typeof 'object' || !content) {
    return null;
  }

  const contents = content
    .replace(/\\blue/g, '<div style="background-color: rgb(229, 246, 255); padding: 2rem;">')
    .replace(/\\cblue/g, '</div>');

  return (
    <div
      className={classNames(styles.richText, className, {
        [styles.light]: light,
      })}
      style={{
        backgroundColor,
        height: backgroundColor ? '100%' : 'auto',
      }}
    >
      {parse(contents, options())}
      {children}
    </div>
  );
};

export default RichText;
